import { DataChannelOverride, FlagOverride, EnvironmentFlags } from '../models/EnvironmentModels';
import { ReleasePipelineSource } from '../../AGSS/models/ReleasePipelineSource';

/**
 * Utility functions for encoding and decoding DataChannelOverride objects
 * for clipboard operations. This allows filters to be copied between environments.
 */

export interface EncodedFilterData {
    name: string;
    enabled: boolean;
    active: boolean;
    workLane: number | null | undefined;
    pipeline: number | null | undefined;
    region: number | null | undefined;
    serviceTreeId: string | null | undefined;
    accountName: string | null | undefined;
    projectName: string | null | undefined;
    branchName: string | null | undefined;
    releaseDefinitionId: string | null | undefined;
    pipelineSourceType: number | null | undefined;
    tags: string[] | undefined;
    startTime: string | null | undefined;
    endTime: string | null | undefined;
}

/**
 * Encodes a DataChannelOverride into a base64 string safe for clipboard/URL
 * @param override The override to encode
 * @returns Base64 encoded string
 */
export function encodeFilterToClipboard(override: DataChannelOverride): string {
    const data: EncodedFilterData = {
        name: override.name ?? '',
        enabled: override.enabled ?? true,
        active: override.active ?? false,
        workLane: override.workLane ?? null,
        pipeline: override.pipeline ?? null,
        region: override.region ?? null,
        serviceTreeId: override.serviceTreeId ?? null,
        accountName: override.accountName ?? null,
        projectName: override.projectName ?? null,
        branchName: override.branchName ?? null,
        releaseDefinitionId: override.releaseDefinitionId ?? null,
        pipelineSourceType: override.pipelineSourceType ?? null,
        tags: override.tags ?? [],
        startTime: override.startTime ?? null,
        endTime: override.endTime ?? null,
    };
    
    const jsonString = JSON.stringify(data);
    return btoa(encodeURIComponent(jsonString));
}

/**
 * Decodes a base64 string into a DataChannelOverride
 * @param encoded The base64 encoded string
 * @returns Decoded DataChannelOverride or null if invalid
 */
export function decodeFilterFromClipboard(encoded: string): DataChannelOverride | null {
    try {
        const jsonString = decodeURIComponent(atob(encoded));
        const data: EncodedFilterData = JSON.parse(jsonString);
        
        // Validate basic structure - ensure it's a valid object
        if (typeof data !== 'object' || data === null) {
            return null;
        }

        // Validate enabled is present and boolean (or default to true)
        if (data.enabled !== undefined && typeof data.enabled !== 'boolean') {
            return null;
        }
        
        const override: DataChannelOverride = {
            name: typeof data.name === 'string' ? data.name : '',
            enabled: data.enabled ?? true,
            active: data.active ?? false,
            workLane: data.workLane,
            pipeline: data.pipeline,
            region: data.region,
            serviceTreeId: data.serviceTreeId,
            accountName: data.accountName,
            projectName: data.projectName,
            branchName: data.branchName,
            releaseDefinitionId: data.releaseDefinitionId,
            pipelineSourceType: data.pipelineSourceType,
            tags: data.tags ?? [],
            startTime: data.startTime ?? null,
            endTime: data.endTime ?? null,
        };
        
        return override;
    } catch (error) {
        console.error('Failed to decode filter from clipboard:', error);
        return null;
    }
}

/**
 * Encodes a FlagOverride into a base64 string safe for clipboard
 * @param override The flag override to encode
 * @returns Base64 encoded string
 */
export function encodeFlagOverrideToClipboard(override: FlagOverride): string {
    const data = {
        _type: 'FlagOverride',
        name: override.name ?? '',
        enabled: override.enabled ?? true,
        active: override.active ?? false,
        serviceTreeId: override.serviceTreeId ?? null,
        accountName: override.accountName ?? null,
        projectName: override.projectName ?? null,
        branchName: override.branchName ?? null,
        releaseDefinitionId: override.releaseDefinitionId ?? null,
        pipelineSourceType: override.pipelineSourceType ?? null,
        tags: override.tags ?? [],
        startTime: override.startTime ?? null,
        endTime: override.endTime ?? null,
        featureFlags: override.featureFlags ?? {},
    };

    const jsonString = JSON.stringify(data);
    return btoa(encodeURIComponent(jsonString));
}

/**
 * Decodes a base64 string into a FlagOverride
 * @param encoded The base64 encoded string
 * @returns Decoded FlagOverride or null if invalid
 */
export function decodeFlagOverrideFromClipboard(encoded: string): FlagOverride | null {
    try {
        const jsonString = decodeURIComponent(atob(encoded));
        const data = JSON.parse(jsonString);

        // Must have featureFlags object or _type marker to distinguish from DataChannelOverride
        if (!data.featureFlags && data._type !== 'FlagOverride') {
            return null;
        }

        // Validate enabled is present and boolean (or default to true)
        if (data.enabled !== undefined && typeof data.enabled !== 'boolean') {
            return null;
        }

        const override: FlagOverride = {
            name: typeof data.name === 'string' ? data.name : '',
            enabled: data.enabled ?? true,
            active: data.active ?? false,
            serviceTreeId: data.serviceTreeId,
            accountName: data.accountName,
            projectName: data.projectName,
            branchName: data.branchName,
            releaseDefinitionId: data.releaseDefinitionId,
            pipelineSourceType: data.pipelineSourceType,
            tags: data.tags ?? [],
            startTime: data.startTime ?? null,
            endTime: data.endTime ?? null,
            featureFlags: data.featureFlags ?? {},
        };

        return override;
    } catch (error) {
        console.error('Failed to decode flag override from clipboard:', error);
        return null;
    }
}

/**
 * Parses an Azure DevOps URL and extracts pipeline information
 * @param url The Azure DevOps URL to parse
 * @returns Parsed information or null if invalid
 */
export interface ParsedPipelineInfo {
    accountName: string;
    projectName: string;
    releaseDefinitionId: string;
    pipelineSourceType: ReleasePipelineSource;
}

export function parseAzureDevOpsUrl(url: string): ParsedPipelineInfo | null {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        const pathname = urlObj.pathname;
        const searchParams = urlObj.searchParams;
        
        let accountName: string | null = null;
        let projectName: string | null = null;
        let releaseDefinitionId: string | null = null;
        let pipelineSourceType: ReleasePipelineSource | null = null;
        
        // Extract account name and project from hostname + path
        if (hostname.includes('dev.azure.com')) {
            // Format: https://dev.azure.com/{org}/{project}/_build?definitionId=...
            const pathParts = pathname.split('/').filter(p => p);
            if (pathParts.length >= 2) {
                accountName = pathParts[0];
                projectName = pathParts[1];
            }
        } else if (hostname.includes('visualstudio.com')) {
            // Format: https://{org}.visualstudio.com/{project}/_release?definitionId=...
            const subdomain = hostname.split('.')[0];
            accountName = subdomain;
            const pathParts = pathname.split('/').filter(p => p);
            if (pathParts.length >= 1) {
                projectName = pathParts[0];
            }
        }
        
        // Determine pipeline type from path segments (_build = YAML, _release/_releaseProgress = Classic)
        const isYaml = pathname.includes('/_build');
        const isClassic = pathname.includes('/_release') || pathname.includes('/_releaseProgress');

        if (isYaml) {
            pipelineSourceType = ReleasePipelineSource.YAML;
            // YAML URLs use definitionId for pipeline list, buildId for details
            if (searchParams.has('definitionId')) {
                releaseDefinitionId = searchParams.get('definitionId');
            } else if (searchParams.has('buildId')) {
                releaseDefinitionId = searchParams.get('buildId');
            }
        } else if (isClassic) {
            pipelineSourceType = ReleasePipelineSource.Classic;
            // Classic URLs use definitionId for pipeline list, releaseId for details
            if (searchParams.has('definitionId')) {
                releaseDefinitionId = searchParams.get('definitionId');
            } else if (searchParams.has('releaseId')) {
                releaseDefinitionId = searchParams.get('releaseId');
            }
        }
        
        // Validate we have all required fields
        if (!accountName || !projectName || !releaseDefinitionId || pipelineSourceType === null) {
            return null;
        }
        
        return {
            accountName,
            projectName: decodeURIComponent(projectName),
            releaseDefinitionId,
            pipelineSourceType,
        };
    } catch (error) {
        console.error('Failed to parse Azure DevOps URL:', error);
        return null;
    }
}
